import React from 'react';

import type { EChartsOption } from 'echarts';
import { reportSurfaces } from '~/configs/unifiedAnalyticsTheme';

import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import LPCTrend from './LPCTrend';
import { type Action, type Dashboard, type Selection, markOpacity } from './actions';
import type { buildDetails } from './details';
import {
  type Filters,
  type Model,
  type Totals,
  changeLabel,
  days,
  finite,
  format,
  scorecardChange,
  sum,
} from './model';

const BLUE = '#1f77b4';
const GRAY = '#888888';
const ROOMS = ['#bcbd22', '#f7b6d2', '#9edae5', '#aec7e8', '#9467bd', '#8c564b', '#2ca02c', '#ff7f0e', '#c7c7c7'];
const CHANNELS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#7f7f7f'];
type Detail = ReturnType<typeof buildDetails>;
type Props = {
  model: Model;
  details: Detail;
  filters: Filters;
  tab: number;
  currentOnly?: boolean;
  currency: string;
  selection: Selection;
  dashboard: Dashboard;
  onSelect: (action: Action) => void;
};
type MetricColumn = { id: string; label: string; unit?: string; current: number | null; comparison: number | null };

/** Tableau revision 4.2 dashboard zones; arithmetic remains in the validated model. */
export default function LPCReport({
  model,
  details,
  filters,
  tab,
  currentOnly = tab === 2,
  currency,
  selection,
  dashboard,
  onSelect,
}: Props) {
  const theme = useTheme();
  const surfaces = reportSurfaces(theme);
  const dark = theme.palette.mode === 'dark';
  const ink = dark ? theme.palette.text.primary : '#111';
  const better = dark ? theme.palette.success.light : '#267041';
  const worse = dark ? theme.palette.error.light : '#c50943';
  const pcm = tab >= 3;
  const sides: Array<'current' | 'comparison'> = currentOnly ? ['current'] : ['current', 'comparison'];
  const range = `${filters.from}–${filters.to}${currentOnly ? '' : ` / ${filters.cmpFrom}–${filters.cmpTo}`}`;
  const money = (value: number | null) =>
    finite(value) ? new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value) : 'Unknown';
  const display = (value: number | null, column: MetricColumn) => {
    if (!finite(value)) {
      return 'Unknown';
    }
    const decimals =
      column.id === 'lead'
        ? 0
        : column.id === 'savings'
          ? 1
          : ['alos', 'mpv', 'conversion', 'parity'].includes(column.id)
            ? 2
            : column.unit === 'percent'
              ? 1
              : 0;
    return new Intl.NumberFormat('en-AU', {
      style: column.unit === 'percent' ? 'percent' : 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };
  const column = (id: keyof Totals, label: string, unit?: string): MetricColumn => ({
    id,
    label,
    unit,
    current: model.current.totals[id],
    comparison: model.comparison.totals[id],
  });
  const columns: Array<MetricColumn> = pcm
    ? [
        column('margin', 'GM AUD (AR)', 'money'),
        column('views', 'OFFER VIEWS'),
        column('conversion', 'CONVERSION RATE', 'percent'),
        column('mpv', 'MARGIN PER VIEW', 'money'),
        column('newMembers', 'NEW LUXPLUS+ MEMBERSHIPS'),
        column('savings', 'AVG LUXPLUS+ SAVINGS', 'money'),
        {
          id: 'regionBookings',
          label: 'Bookings Share of Region %',
          unit: 'percent',
          current: details.regionSummary.current.bookings,
          comparison: details.regionSummary.comparison.bookings,
        },
        {
          id: 'regionViews',
          label: 'Views Share of Region %',
          unit: 'percent',
          current: details.regionSummary.current.views,
          comparison: details.regionSummary.comparison.views,
        },
        column('repeat', 'REPEAT SALES'),
      ]
    : [
        column('nights', 'ROOM NIGHTS'),
        column('revenue', `VENDOR REVENUE (AR) • ${currency}`, 'money'),
        column('orders', 'BOOKINGS'),
        column('packages', 'PACKAGES SOLD'),
        column('alos', 'ALOS', 'days'),
        column('lead', 'AVG. LEAD-TIME', 'days'),
        ...(currentOnly
          ? [column('upgrade', '% OF UPGRADES PURCHASED', 'percent')]
          : [
              column('members', '% Luxplus+ Members', 'percent'),
              {
                id: 'parity',
                label: 'Avg Price Difference',
                unit: 'percent',
                current: model.parity.current.priceDiff,
                comparison: model.parity.comparison.priceDiff,
              },
            ]),
        column('adr', `AVG. DAILY RATE • ${currency}`, 'money'),
      ];
  const band = (title: string) => (
    <Typography
      component="h2"
      sx={{
        bgcolor: '#0f464f',
        color: '#fff',
        textAlign: 'center',
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1.2,
        p: '5px 6px',
        minHeight: 25,
      }}
    >
      {title}
    </Typography>
  );
  const chart = (title: string, option: EChartsOption, height = 230, onPick?: Props['onSelect']) => (
    <LPCTrend
      title={title}
      source="Source workbook 3626176 revision 4.2"
      range={range}
      height={height}
      option={option}
      onSelect={
        onPick
          ? ({ product }) => onPick({ type: 'room', value: product, measure: 'nights' })
          : pcm && title.includes('VIEWS')
            ? () => onSelect({ type: 'measure', value: 'views' })
            : undefined
      }
    />
  );
  const lines = (
    names: Array<string>,
    x: Array<number | string>,
    values: Array<Array<number | null>>,
    colors = [BLUE, GRAY],
  ): EChartsOption => ({
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, type: 'scroll', data: names },
    xAxis: { type: 'category', data: x, name: 'Days Since Offer Start', nameLocation: 'middle', nameGap: 24 },
    yAxis: { type: 'value' },
    series: values.map((data, index) => ({
      name: names[index],
      type: 'line',
      showSymbol: false,
      connectNulls: false,
      data,
      itemStyle: { color: colors[index % colors.length] },
      lineStyle: {
        width: 2,
        opacity: markOpacity(selection, dashboard, { room: names[index], measure: pcm ? 'views' : 'nights' }),
      },
    })),
  });
  const names = currentOnly ? ['Current Period'] : ['Current Period', 'Comparison Period'];
  const nights = chart(
    'TOTAL CUMULATIVE ROOM NIGHTS SOLD',
    lines(
      names,
      model.curve.map((p) => p.day),
      [model.curve.map((p) => p.current), ...(!currentOnly ? [model.curve.map((p) => p.comparison)] : [])],
    ),
    225,
  );
  const rooms = chart(
    'TOTAL CUMULATIVE ROOM NIGHTS SOLD BY ROOM TYPE',
    lines(
      model.roomCurves.map((r) => r.name),
      model.roomCurves[0]?.values.map((p) => p.day) || [],
      model.roomCurves.map((r) => r.values.map((p) => p.nights)),
      ROOMS,
    ),
    200,
    onSelect,
  );
  const bars = (title: string, rows: Model['rooms'], metric: 'orders' | 'packages' | 'revenue', lead = false) => (
    <LPCTrend
      title={title}
      source="analytics.sales_with_refunds"
      range={range}
      height={200}
      onSelect={lead ? () => onSelect({ type: 'measure', value: 'orders', scope: 'lead' }) : undefined}
      option={{
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: names },
        xAxis: {
          type: 'category',
          data: rows.map((r) => r.name),
          axisLabel: { interval: 0, fontSize: 11, hideOverlap: true },
        },
        yAxis: {
          type: 'value',
          ...(metric === 'revenue'
            ? {
                axisLabel: {
                  formatter: (value: number) =>
                    new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(value),
                },
              }
            : {}),
        },
        series: names.map((name, i) => ({
          name,
          type: 'bar',
          data: rows.map((r) => (i === 0 ? r.current : r.comparison)[metric]),
          itemStyle: {
            color: i === 0 ? BLUE : GRAY,
            opacity: markOpacity(selection, dashboard, { measure: metric }, lead ? 'lead' : 'dashboard'),
          },
        })),
      }}
    />
  );
  const table = (
    title: string,
    rows: Detail['guestMix'],
    sides: Array<'current' | 'comparison'> = currentOnly ? ['current'] : ['current', 'comparison'],
    room = false,
  ) => (
    <Paper variant="outlined" sx={{ borderRadius: 0, minWidth: 0, overflow: 'hidden' }}>
      {band(title)}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: sides.map(() => 'minmax(0,1fr)').join(' ') },
          gap: 0.5,
        }}
      >
        {sides.map((side) => (
          <TableContainer key={side}>
            <Typography sx={{ bgcolor: surfaces.stripe, textAlign: 'center', fontWeight: 700, fontSize: 12 }}>
              {side === 'current' ? 'Current Period' : 'Comparison Period'}
            </Typography>
            <Table size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{room ? 'Room Type Name' : 'Group'}</TableCell>
                  <TableCell align="right">Packages Sold</TableCell>
                  <TableCell align="right">% of Packages</TableCell>
                  <TableCell align="right">Avg Daily Rate</TableCell>
                  <TableCell align="right">Avg LOS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .filter((row) => row[side].packages !== null)
                  .map((row) => {
                    const value = row[side];
                    const total = rows.reduce((n, r) => n + (r[side].packages || 0), 0);
                    return (
                      <TableRow
                        key={row.name}
                        sx={{
                          opacity: markOpacity(selection, dashboard, {
                            room: room ? row.name : undefined,
                            measure: 'packages',
                          }),
                        }}
                      >
                        <TableCell>
                          {room && !pcm ? (
                            <Button
                              onClick={() => onSelect({ type: 'room', value: row.name, measure: 'packages' })}
                              sx={{ color: 'inherit', textTransform: 'none', p: 0, minWidth: 0, fontSize: 'inherit' }}
                            >
                              {row.name}
                            </Button>
                          ) : (
                            row.name
                          )}
                        </TableCell>
                        <TableCell align="right">{format(value.packages)}</TableCell>
                        <TableCell align="right">
                          {format(total && value.packages !== null ? value.packages / total : null, 'percent')}
                        </TableCell>
                        <TableCell align="right">{money(value.adr)}</TableCell>
                        <TableCell align="right">{format(value.alos)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        ))}
      </Box>
    </Paper>
  );
  const guestClasses = ['Solo', 'Couples', 'Families & Groups'];
  const roomNames = [...new Set(details.roomGuestMix.map((row) => row.name.split(' · ').slice(0, -1).join(' · ')))];
  const roomGuest = (
    <Paper variant="outlined" sx={{ borderRadius: 0, minWidth: 0 }}>
      {band('BY ROOM TYPE • CURRENT OFFER ONLY')}
      <TableContainer tabIndex={0} aria-label="Room type and guest group details">
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>Room Type Name</TableCell>
              {guestClasses.map((group) => (
                <TableCell key={group} colSpan={4} align="center">
                  {group}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {guestClasses.flatMap((group) =>
                ['Packages Sold', 'Avg Daily Rate', 'Avg LOS', 'Avg Lead-Time'].map((label) => (
                  <TableCell key={group + label} align="right">
                    {label}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {roomNames.map((room) => (
              <TableRow key={room}>
                <TableCell>{room}</TableCell>
                {guestClasses.flatMap((group) => {
                  const value = details.roomGuestMix.find((row) => row.name === `${room} · ${group}`)?.current;
                  return [value?.packages, value?.adr, value?.alos, value?.lead].map((n, i) => (
                    <TableCell key={group + i} align="right">
                      {n === null || n === undefined ? '' : i === 1 ? money(n) : format(n)}
                    </TableCell>
                  ));
                })}
              </TableRow>
            ))}
            {sides.map((side) => (
              <TableRow
                key={side}
                sx={{
                  '& .MuiTableCell-root': {
                    bgcolor: side === 'current' ? '#0f464f' : surfaces.stripe,
                    color: side === 'current' ? '#fff' : ink,
                    fontWeight: 700,
                  },
                }}
              >
                <TableCell>{side === 'current' ? 'Grand Total' : 'Comparison Period'}</TableCell>
                {guestClasses.flatMap((group) => {
                  const value = details.guestMix.find((row) => row.name === group)?.[side];
                  return [value?.packages, value?.adr, value?.alos, value?.lead].map((n, i) => (
                    <TableCell key={group + i} align="right">
                      {n === null || n === undefined ? '' : i === 1 ? money(n) : format(n)}
                    </TableCell>
                  ));
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
  const daily = (side: 'current' | 'comparison') => {
    const start = side === 'current' ? filters.from : filters.cmpFrom;
    return model.curve.map((p) =>
      sum(
        model[side].viewRows.filter((r) => days(start, r.date) === p.day),
        (r) => r.views,
      ),
    );
  };
  const dailyViews = chart(
    'DAILY OFFER PAGE VIEWS',
    lines(
      names,
      model.curve.map((p) => p.day),
      [daily('current'), ...(!currentOnly ? [daily('comparison')] : [])],
    ),
    230,
  );
  const channels = chart(
    'CUMULATIVE PAGE VIEWS BY TRAFFIC CHANNEL • CURRENT OFFER ONLY',
    lines(
      details.channelCurves.map((c) => c.name),
      model.curve.map((p) => p.day),
      details.channelCurves.map((c) => c.points.map((p) => p.current)),
      CHANNELS,
    ),
    230,
  );
  const guest = (
    <Paper variant="outlined" sx={{ borderRadius: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {band('GROUP SIZE INSIGHTS')}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: currentOnly ? 'minmax(0,1fr)' : 'repeat(2,minmax(0,1fr))' },
          flex: 1,
        }}
      >
        {sides.map((side) => (
          <LPCTrend
            key={side}
            title={side === 'current' ? 'CURRENT OFFER' : 'COMPARISON PERIOD'}
            range={range}
            source="Packages sold • Solo / Couples / Families & Groups"
            height={200}
            option={{
              tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
              series: [
                {
                  type: 'pie',
                  radius: '45%',
                  data: details.guestMix.map((r) => ({
                    name: r.name,
                    value: r[side].packages,
                    itemStyle: { color: r.name === 'Solo' ? '#1f77b4' : r.name === 'Couples' ? '#ff7f0e' : '#9467bd' },
                  })),
                  label: { formatter: '{b}\n{d}%', fontSize: 10 },
                },
              ],
            }}
          />
        ))}
      </Box>
    </Paper>
  );
  const regional = (views = false) =>
    chart(
      views ? 'Cumulative Views • Share of Region' : '% Share of Region (Bookings) - 7 Day Rolling Average',
      {
        ...lines(
          names,
          (views ? model.regionalViewCurve : model.regionalCurve).map((p) => p.day),
          [
            (views ? model.regionalViewCurve : model.regionalCurve).map((p) => p.current),
            ...(!currentOnly ? [(views ? model.regionalViewCurve : model.regionalCurve).map((p) => p.comparison)] : []),
          ],
        ),
        yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${(v * 100).toFixed(0)}%` } },
      },
      240,
    );
  const parity = chart(
    pcm ? 'CVR & Parity' : 'Avg Price Difference Vs Bookings (Current Period Only)',
    {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      xAxis: {
        type: 'category',
        data: model.priceCurve.map((p) => p.day),
        name: 'Day In Period',
        nameLocation: 'middle',
        nameGap: 25,
      },
      yAxis: [
        { type: 'value', ...(pcm ? { axisLabel: { formatter: (v: number) => `${(v * 100).toFixed(2)}%` } } : {}) },
        { type: 'value', axisLabel: { formatter: (v: number) => `${(v * 100).toFixed(0)}%` } },
      ],
      series: [
        {
          name: pcm ? 'CVR' : 'Bookings',
          type: pcm ? 'line' : 'bar',
          data: pcm ? details.parityCvr.map((p) => p.cvr) : model.priceCurve.map((p) => p.bookings),
          itemStyle: { color: '#76b7b2' },
          showSymbol: false,
        },
        {
          name: 'Avg Price Diff %',
          type: 'line',
          yAxisIndex: 1,
          data: model.priceCurve.map((p) => p.priceDiff),
          itemStyle: { color: '#d62728' },
          showSymbol: false,
        },
      ],
    },
    pcm ? 240 : 405,
  );
  const months = (
    <Paper variant="outlined" sx={{ borderRadius: 0 }}>
      {band('CHECK-IN BY MONTH')}
      <Table size="small" sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>Month</TableCell>
            {names.map((n) => (
              <TableCell key={n} align="right">
                {n} Bookings (AR)
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {details.monthRows.map((r) => (
            <TableRow key={r.name}>
              <TableCell>{r.name}</TableCell>
              <TableCell align="right">{format(r.current?.afterRefund.orders)}</TableCell>
              {!currentOnly && <TableCell align="right">{format(r.comparison?.afterRefund.orders)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
  const checkinClassChart = chart(
    'CHECK-IN BY MONTH • BOOKINGS (AR)',
    {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, data: names },
      xAxis: {
        type: 'category',
        data: details.checkinClassRows.map((row) => row.name.split(' · ').join('\n')),
        axisLabel: { interval: 0, fontSize: 10, hideOverlap: true },
      },
      yAxis: { type: 'value', name: 'Bookings (AR)' },
      series: sides.map((side, i) => ({
        name: i === 0 ? 'Current Period' : 'Comparison Period',
        type: 'bar',
        data: details.checkinClassRows.map((row) => row[side]),
        itemStyle: { color: i === 0 ? BLUE : GRAY },
      })),
    },
    245,
  );
  const checkinRevenueChart = chart(
    'CHECK-IN BY MONTH • VENDOR REVENUE (AR)',
    {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, data: names },
      xAxis: {
        type: 'category',
        data: details.monthRows.map((row) => row.name),
        axisLabel: { interval: 0, fontSize: 10, hideOverlap: true },
      },
      yAxis: {
        type: 'value',
        name: currency,
        axisLabel: {
          formatter: (value: number) =>
            new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(value),
        },
      },
      series: sides.map((side, i) => ({
        name: i === 0 ? 'Current Period' : 'Comparison Period',
        type: 'bar',
        data: details.monthRows.map((row) => row[side]?.totals.revenue ?? null),
        itemStyle: { color: i === 0 ? BLUE : GRAY },
      })),
    },
    210,
  );
  const packageChart = chart(
    'PACKAGE TYPE • ROOM NIGHTS',
    {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0, data: names },
      xAxis: {
        type: 'category',
        data: details.packageMix.map((row) => row.name),
        axisLabel: { interval: 0, fontSize: 10, hideOverlap: true },
      },
      yAxis: { type: 'value' },
      series: sides.map((side, i) => ({
        name: i === 0 ? 'Current Period' : 'Comparison Period',
        type: 'bar',
        data: details.packageMix.map((row) => row[side].nights),
        itemStyle: { color: i === 0 ? BLUE : GRAY },
      })),
    },
    210,
  );
  const moduleRow = (id: string, left: React.ReactNode, right: React.ReactNode) => (
    <Box
      data-lpc-row={id}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0,1fr)',
          lg: currentOnly ? 'repeat(2,minmax(0,1fr))' : 'minmax(0,40fr) minmax(0,60fr)',
        },
        gap: 1,
        mt: 1,
        alignItems: 'stretch',
        '& > .lpc-module': { minWidth: 0, display: 'flex', flexDirection: 'column', bgcolor: surfaces.paper },
        '& > .lpc-module > .MuiPaper-root': { flex: 1 },
        '& .lpc-module > div > .MuiPaper-root': { flex: 1 },
      }}
    >
      <Box className="lpc-module">{left}</Box>
      <Box className="lpc-module">{right}</Box>
    </Box>
  );
  return (
    <Box
      data-lpc-report={tab}
      sx={{
        fontFamily: 'inherit',
        '& .MuiPaper-root': {
          boxShadow: 'none',
          borderRadius: 0,
          borderColor: surfaces.rule,
          bgcolor: surfaces.paper,
          minWidth: 0,
        },
        '& .MuiTableContainer-root': { maxWidth: '100%', overflowX: 'auto' },
        '& .MuiTableCell-root': {
          p: '3px 5px',
          fontFamily: 'inherit',
          fontSize: 11,
          lineHeight: 1.15,
          borderBottom: 0,
        },
        '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: surfaces.stripe, color: ink, fontWeight: 400 },
        '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': { bgcolor: surfaces.stripe },
      }}
    >
      {tab !== 4 && (
        <TableContainer sx={{ mb: 1, maxWidth: '100%', overflowX: 'auto' }}>
          <Table
            aria-label="Campaign scorecard"
            size="small"
            sx={{
              minWidth: 1000,
              '& .MuiButton-root': { fontSize: 'inherit !important', lineHeight: 'inherit', minHeight: 0 },
              '& .MuiTableBody-root .MuiTableCell-root': {
                fontSize: 18,
                p: '5px 4px',
                lineHeight: 1.15,
                border: `3px solid ${surfaces.page}`,
                bgcolor: surfaces.paper,
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontSize: 10,
                fontWeight: 600,
                p: '5px 4px',
                lineHeight: 1.2,
                border: `3px solid ${surfaces.page}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((c) => (
                  <TableCell key={c.id} align="center" title={c.label}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(currentOnly ? (['current'] as const) : (['current', 'comparison', 'change'] as const)).map((side) => (
                <TableRow key={side}>
                  <TableCell
                    sx={{ bgcolor: '#0f464f !important', color: '#fff', fontWeight: 600, fontSize: '12px !important' }}
                  >
                    {side === 'current' ? 'Current Period' : side === 'comparison' ? 'Comparison' : '% Difference'}
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell
                      key={c.id}
                      align="center"
                      sx={{
                        fontSize: 16,
                        fontWeight: 700,
                        opacity: markOpacity(selection, dashboard, { measure: c.id }),
                        color:
                          side === 'change' && finite(c.current) && finite(c.comparison)
                            ? c.current >= c.comparison
                              ? better
                              : worse
                            : ink,
                      }}
                    >
                      {side === 'change' ? (
                        ['parity', 'regionBookings', 'regionViews'].includes(c.id) ? (
                          changeLabel(c.current, c.comparison, true)
                        ) : (
                          scorecardChange(c.id as keyof Totals, c.current, c.comparison)
                        )
                      ) : (
                        <Button
                          disabled={tab === 1}
                          onClick={() => onSelect({ type: 'measure', value: c.id })}
                          sx={{
                            color: 'inherit',
                            font: 'inherit',
                            p: 0,
                            minWidth: 0,
                            '&.Mui-disabled': { color: 'inherit' },
                          }}
                        >
                          {display(c[side], c)}
                        </Button>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {tab === 4 ? (
        <>
          {moduleRow('pcm2-overview', dailyViews, guest)}
          {moduleRow('pcm2-guests', channels, roomGuest)}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(20,minmax(0,1fr))',
              gap: 1,
              mt: 1,
              '& > div': { minWidth: 0, display: 'flex', flexDirection: 'column' },
              '& > div > .MuiPaper-root': { flex: 1 },
            }}
          >
            <Box sx={{ gridColumn: { xs: 'span 20', lg: 'span 10' } }}>{checkinClassChart}</Box>
            <Box sx={{ gridColumn: { xs: 'span 20', lg: 'span 10' } }}>
              {bars('PACKAGES SOLD BY LEAD TIME', model.leads, 'orders', true)}
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 20', lg: 'span 10' } }}>{checkinRevenueChart}</Box>
            <Box sx={{ gridColumn: { xs: 'span 20', lg: 'span 7' } }}>
              {bars('VENDOR REVENUE BY ROOM TYPE', model.rooms, 'revenue')}
            </Box>
            <Box sx={{ gridColumn: { xs: 'span 20', lg: 'span 3' } }}>{packageChart}</Box>
          </Box>
        </>
      ) : pcm ? (
        <>
          {moduleRow('pcm-overview', dailyViews, guest)}
          {moduleRow('pcm-guests', channels, roomGuest)}
          {moduleRow(
            'pcm-region',
            parity,
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2,minmax(0,1fr))' },
                gap: 1,
                minWidth: 0,
                flex: 1,
              }}
            >
              {regional()}
              {regional(true)}
            </Box>,
          )}
        </>
      ) : (
        <>
          {moduleRow(
            'vendor-overview',
            nights,
            table('ROOM TYPES', details.roomShares, currentOnly ? ['current'] : ['current', 'comparison'], true),
          )}
          {moduleRow(
            'vendor-room-detail',
            rooms,
            currentOnly ? (
              table('CUSTOMER DETAILS', details.customerDetails)
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>{parity}</Box>
                <Typography sx={{ textAlign: 'right', fontSize: 11, color: worse, pt: 0.5 }}>
                  {model.parity.current.coverage / model.parity.current.totalDays <= 0.75 ? 'Low Parity Data • ' : ''}
                  {model.parity.current.coverage} of {model.parity.current.totalDays} days
                </Typography>
              </>
            ),
          )}
          {moduleRow(
            'vendor-bookings',
            bars('PACKAGES SOLD BY CHECK IN DAY-OF-WEEK', model.weekdays, 'packages'),
            currentOnly ? bars('PACKAGES SOLD BY LEAD TIME', model.leads, 'orders', true) : regional(),
          )}
          <Box sx={{ mt: 1 }}>
            {currentOnly ? months : bars('PACKAGES SOLD BY LEAD TIME', model.leads, 'orders', true)}
          </Box>
        </>
      )}
    </Box>
  );
}
